import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../api/api';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Button } from '../../components/ui/Button';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';

const PercentBudget = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [budgetData, setBudgetData] = useState([]);
  const [markets, setMarkets] = useState([]); // Market listesi
  const [hotels, setHotels] = useState([]); // Otel listesi
  const [budgetTypes, setBudgetTypes] = useState([]); // Bütçe Tipi listesi
  const [groupedData, setGroupedData] = useState({}); // { maingroup_name: { totalAmount, years, months } }
  const [maingroupRows, setMaingroupRows] = useState({}); // { "maingroup": [{ id, market, hotel, budget_type, percentage }] }
  const [rowIdCounter, setRowIdCounter] = useState(1);
  const [selectedYear, setSelectedYear] = useState(null); // Seçilen yıl

  // Tüm verileri senkron bir loading state ile çek
  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchMarkets(),
        fetchHotels(),
        fetchBudgetTypes(),
        fetchBudgetData(),
        fetchSavedPercentages()
      ]);
    } catch (err) {
      console.error('Veri yükleme hatası:', err);
      setError('Veriler yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    // Global yükleme sinyali gönder
    window.dispatchEvent(new CustomEvent('app_loading', { detail: loading }));
  }, [loading]);

  // Market listesini çek
  const fetchMarkets = async () => {
    try {
      const response = await apiRequest('/marketing-budget/markets/', 'GET');
      if (response && Array.isArray(response)) {
        setMarkets(response);
      } else if (response && response.results) {
        setMarkets(response.results);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  // Otel listesini çek
  const fetchHotels = async () => {
    try {
      const response = await apiRequest('/marketing-budget/hotels/', 'GET');
      if (response && Array.isArray(response)) {
        setHotels(response);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  // Bütçe Tipi listesini çek
  const fetchBudgetTypes = async () => {
    try {
      const response = await apiRequest('/marketing-budget/types/', 'GET');
      if (response && Array.isArray(response)) {
        setBudgetTypes(response);
      } else if (response && response.results) {
        setBudgetTypes(response.results);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  // Kaydedilmiş yüzdeleri çek
  const fetchSavedPercentages = async () => {
    try {
      const response = await apiRequest('/marketing-budget/percent-budget/', 'GET');

      if (response && Array.isArray(response)) {
        const rowsByMaingroup = {};
        let maxId = 0;

        response.forEach(item => {
          const maingroup = item.maingroup_name;
          if (!rowsByMaingroup[maingroup]) {
            rowsByMaingroup[maingroup] = [];
          }

          const rowId = ++maxId;
          rowsByMaingroup[maingroup].push({
            id: rowId,
            market: item.market_name || '',
            hotel: item.hotel_name || '',
            budget_type: item.budget_type || '',
            percentage: item.percentage.toString(),
            budget_year: item.budget_year,
            budget_month: item.budget_month
          });
        });

        setRowIdCounter(maxId + 1);
        setMaingroupRows(rowsByMaingroup);
      }
    } catch (err) {
      // Silently handle error
    }
  };

  const fetchBudgetData = async () => {
    try {
      const response = await apiRequest('/marketing-budget/registries/', 'GET');

      if (response && Array.isArray(response)) {
        setBudgetData(response);
        organizeData(response);
      }
    } catch (err) {
      throw err;
    }
  };

  // Verileri sadece ana grup bazında organize et (toplam tutar)
  const organizeData = (data) => {
    const grouped = {};

    data.forEach(item => {
      const { maingroup_name, budget_amount, budget_year, budget_month } = item;

      if (!maingroup_name) return;

      // Ana grup yoksa oluştur
      if (!grouped[maingroup_name]) {
        grouped[maingroup_name] = {
          totalAmount: 0,
          years: new Set(),
          months: new Set()
        };
      }

      // Bütçe miktarını ekle
      const amount = parseFloat(budget_amount) || 0;
      grouped[maingroup_name].totalAmount += amount;

      // Yıl ve ay bilgilerini ekle
      if (budget_year) {
        grouped[maingroup_name].years.add(budget_year);
      }
      if (budget_month) {
        grouped[maingroup_name].months.add(budget_month);
      }
    });

    // Set'leri array'e çevir ve tüm yılları topla
    const allYears = new Set();
    Object.keys(grouped).forEach(maingroup => {
      grouped[maingroup].years = Array.from(grouped[maingroup].years).sort();
      grouped[maingroup].months = Array.from(grouped[maingroup].months).sort();
      // Tüm yılları topla
      grouped[maingroup].years.forEach(year => allYears.add(year));
    });

    setGroupedData(grouped);

    // Eğer yıl seçilmemişse, en yeni yılı otomatik seç
    if (!selectedYear && allYears.size > 0) {
      const sortedYears = Array.from(allYears).sort((a, b) => b - a); // En yeni yıl önce
      setSelectedYear(sortedYears[0]);
    }

    // Eğer bir ana grup için hiç satır yoksa, boş bir satır ekle
    setMaingroupRows(prev => {
      const newRows = { ...prev };
      Object.keys(grouped).forEach(maingroup => {
        if (!newRows[maingroup] || newRows[maingroup].length === 0) {
          newRows[maingroup] = [{
            id: rowIdCounter,
            market: '',
            hotel: '',
            budget_type: '',
            percentage: ''
          }];
          setRowIdCounter(c => c + 1);
        }
      });
      return newRows;
    });
  };

  // Satır ekle
  const handleAddRow = (maingroup) => {
    const newRow = {
      id: rowIdCounter,
      market: '',
      hotel: '',
      budget_type: '',
      percentage: ''
    };
    setRowIdCounter(c => c + 1);
    setMaingroupRows(prev => ({
      ...prev,
      [maingroup]: [...(prev[maingroup] || []), newRow]
    }));
  };

  // Satır sil
  const handleRemoveRow = (maingroup, rowId) => {
    setMaingroupRows(prev => ({
      ...prev,
      [maingroup]: (prev[maingroup] || []).filter(row => row.id !== rowId)
    }));
  };

  // Satır değişikliği
  const handleRowChange = (maingroup, rowId, field, value) => {
    setMaingroupRows(prev => ({
      ...prev,
      [maingroup]: (prev[maingroup] || []).map(row =>
        row.id === rowId ? { ...row, [field]: value } : row
      )
    }));
  };

  // Ana grup bazında yüzde validasyonu
  const validatePercentages = (maingroup) => {
    const rows = maingroupRows[maingroup] || [];
    let total = 0;
    const errors = [];
    let filledRowsCount = 0; // Doldurulmuş satır sayısı

    rows.forEach(row => {
      // Sadece doldurulmuş satırları kontrol et
      const hasMarket = row.market && row.market.trim() !== '';
      const hasPercentage = row.percentage && parseFloat(row.percentage) > 0;

      // Eğer market veya percentage varsa, bu satır doldurulmuş demektir
      if (hasMarket || hasPercentage) {
        filledRowsCount++;

        // Doldurulmuş satırlar için validation
        if (!hasMarket) {
          errors.push('Market seçimi yapılmamış satırlar var');
        }

        const percentage = parseFloat(row.percentage) || 0;
        if (percentage <= 0) {
          errors.push('Yüzde değeri girilmemiş satırlar var');
        }

        total += percentage;
      }
      // Hotel opsiyonel, kontrol etmiyoruz
    });

    // Eğer hiç doldurulmuş satır yoksa, validation geçerli (kullanıcı bu grup için kayıt yapmak istemiyor)
    if (filledRowsCount === 0) {
      return {
        isValid: true,
        total: 0,
        errors: []
      };
    }

    return {
      isValid: errors.length === 0, // Toplam yüzde kontrolünü kaldırdık, sadece hataları kontrol ediyoruz
      total: total,
      errors: errors
    };
  };

  // Kaydet
  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    // Tüm ana gruplar için validasyon (sadece doldurulmuş satırlar için)
    const validationErrors = [];
    Object.keys(groupedData).forEach(maingroup => {
      const validation = validatePercentages(maingroup);
      if (!validation.isValid && validation.errors.length > 0) {
        // Sadece gerçek hataları göster (boş satırları değil)
        validationErrors.push(`${maingroup}: ${validation.errors[0]}`);
      }
    });

    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      return;
    }

    setLoading(true);
    try {
      // Yüzde verilerini hazırla
      const percentagesWithDetails = {};

      Object.keys(groupedData).forEach(maingroup => {
        const rows = maingroupRows[maingroup] || [];
        const groupData = groupedData[maingroup];

        // Yıl ve ay bilgilerini al (seçilen yıl veya ilk değer)
        const budgetYear = selectedYear || (groupData.years && groupData.years.length > 0
          ? groupData.years[0]
          : null);
        const budgetMonth = groupData.months && groupData.months.length > 0
          ? groupData.months[0]
          : null;

        rows.forEach(row => {
          // Sadece market ve percentage dolu olan satırları kaydet
          // Hotel opsiyonel, market zorunlu
          if (!row.market || !row.market.trim() || !row.percentage || parseFloat(row.percentage) <= 0) {
            return; // Boş satırları atla
          }

          // Key format: "maingroup_market_hotel" (hotel yoksa "maingroup_market")
          const hotelPart = row.hotel && row.hotel.trim() ? `_${row.hotel}` : '';
          const key = `${maingroup}_${row.market}${hotelPart}`;

          percentagesWithDetails[key] = {
            percentage: parseFloat(row.percentage),
            budget_year: budgetYear,
            budget_month: budgetMonth,
            hotel_name: row.hotel && row.hotel.trim() ? row.hotel : null, // Hotel opsiyonel
            budget_type: row.budget_type && row.budget_type.trim() ? row.budget_type : null, // Bütçe Tipi opsiyonel
            market_name: row.market === 'Belirtilmemiş' ? null : row.market
          };
        });
      });

      // Backend endpoint'e kaydetme isteği gönder
      const response = await apiRequest(
        '/marketing-budget/percent-budget/bulk_update/',
        'POST',
        { percentages: percentagesWithDetails }
      );

      if (response.errors && response.errors.length > 0) {
        setError(`Bazı kayıtlar kaydedilemedi:\n${response.errors.join('\n')}`);
      } else {
        setSuccess(`Yüzdeler başarıyla kaydedildi! (${response.created} yeni, ${response.updated} güncellendi)`);
        setTimeout(() => setSuccess(null), 5000);
        fetchSavedPercentages(); // Kaydedilenleri tekrar çek
      }
    } catch (err) {
      // Silently handle error
      setError('Yüzdeler kaydedilirken bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const sortedMainGroups = Object.keys(groupedData).sort();

  // Tüm yılları topla (dropdown için)
  const allAvailableYears = new Set();
  Object.keys(groupedData).forEach(maingroup => {
    if (groupedData[maingroup].years) {
      groupedData[maingroup].years.forEach(year => allAvailableYears.add(year));
    }
  });
  const sortedYears = Array.from(allAvailableYears).sort((a, b) => b - a); // En yeni yıl önce

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Percent Budget
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Ana grup bazında toplam bütçe üzerinden market ve otel seçerek yüzde belirleyin.
            </p>
          </div>
          <ActionToolbar className="gap-3">
            {/* Yıl Seçim Alanı */}
            <div className="flex items-center space-x-2">
              <label htmlFor="year-select" className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Yıl:
              </label>
              <select
                id="year-select"
                value={selectedYear || ''}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[100px]"
              >
                <option value="">Tüm Yıllar</option>
                {sortedYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <Button
              type="button"
              onClick={() => window.history.back()}
              variant="outline"
              className="h-10 px-4"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Geri Dön
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
            >
              <CheckIcon className="w-5 h-5 mr-2" />
              Kaydet
            </Button>
          </ActionToolbar>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900 border border-green-400 text-green-700 dark:text-green-300 rounded-lg flex items-center">
            <CheckCircleIcon className="w-5 h-5 mr-2" />
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-300 rounded-lg flex items-start">
            <ExclamationTriangleIcon className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
            <div className="whitespace-pre-line">{error}</div>
          </div>
        )}



        {/* Budget Groups */}
        {!loading && sortedMainGroups.length > 0 && (
          <div className="space-y-6">
            {sortedMainGroups.map(maingroup => {
              const group = groupedData[maingroup];
              const rows = maingroupRows[maingroup] || [];
              const validation = validatePercentages(maingroup);

              return (
                <div
                  key={maingroup}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
                >
                  {/* Ana Grup Header */}
                  <div className="bg-blue-600 dark:bg-blue-800 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-white">{maingroup}</h2>
                      <div className="flex items-center space-x-4">
                        <span className="text-blue-100 text-sm">
                          Toplam: {formatCurrency(group.totalAmount)}
                        </span>
                        <span
                          className={`text-sm font-semibold ${validation.isValid
                            ? 'text-green-200'
                            : 'text-yellow-200'
                            }`}
                        >
                          Toplam Yüzde: {validation.total.toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Market
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Otel
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Bütçe Tipi
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Yüzde (%)
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            Hesaplanan Tutar
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            İşlemler
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {rows.map(row => {
                          const percentage = parseFloat(row.percentage) || 0;
                          const calculatedAmount = (group.totalAmount * percentage) / 100;

                          return (
                            <tr
                              key={row.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={row.market || ''}
                                  onChange={(e) => handleRowChange(maingroup, row.id, 'market', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Market Seçiniz</option>
                                  {markets.map(m => (
                                    <option key={m.id} value={m.market_name || m.budget_market_name}>
                                      {m.market_name || m.budget_market_name}
                                    </option>
                                  ))}
                                  <option value="Belirtilmemiş">Belirtilmemiş</option>
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={row.hotel || ''}
                                  onChange={(e) => handleRowChange(maingroup, row.id, 'hotel', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Otel Seçiniz</option>
                                  {hotels.map(h => (
                                    <option key={h.id} value={h.hotel_name}>
                                      {h.hotel_name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={row.budget_type || ''}
                                  onChange={(e) => handleRowChange(maingroup, row.id, 'budget_type', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="">Bütçe Tipi Seçiniz</option>
                                  {budgetTypes.map(bt => (
                                    <option key={bt.id} value={bt.budget_type}>
                                      {bt.budget_type}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={row.percentage || ''}
                                  onChange={(e) => handleRowChange(maingroup, row.id, 'percentage', e.target.value)}
                                  className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="0.00"
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                                {formatCurrency(calculatedAmount)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <IconActionButton
                                  icon={TrashIcon}
                                  onClick={() => handleRemoveRow(maingroup, row.id)}
                                  title="Satırı sil"
                                  disabled={rows.length === 1}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Add Row Button */}
                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                    <Button
                      type="button"
                      onClick={() => handleAddRow(maingroup)}
                      variant="outline"
                      className="h-9 px-4 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 border-blue-300 dark:border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      Satır Ekle
                    </Button>
                  </div>

                  {/* Validation Warning */}
                  {!validation.isValid && (
                    <div className="px-6 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-center text-yellow-800 dark:text-yellow-200 text-sm">
                        <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                        <span>
                          {validation.errors.length > 0
                            ? validation.errors[0]
                            : `Toplam yüzde ${validation.total.toFixed(2)}%`
                          }
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && sortedMainGroups.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Gösterilecek bütçe verisi bulunamadı.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PercentBudget;
